import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useApi } from '../../api';
import { fmtTime } from './statusUtils';
import { PresenceTag } from './AttendanceTags';
import EmployeeAvatar from './EmployeeAvatar';
import {
  TableCard, TableSkeleton, TableEmpty, TableError, Pager, FilterBar, TH, TD, ROW,
  StatTile, StatTiles, usePagination, PageSizeSelect, RECORD_PAGE_SIZE,
  FILTER_BTN, FilterCount, FilterActions,
  useSort, SortableTH, timeKey, PersonCell, TimeInsideBar,
} from './tableUi';
import {
  FilterColumn, FilterSection, FilterSearch, FILTER_CONTROL, PageWithFilters, useFilterColumn,
} from '../../components/layout/FilterColumn';

/** Columns the presence board can be ordered by — name, department, and the two clock times. */
const PRESENCE_SORTS = {
  name: (r: any) => r.name || '',
  department: (r: any) => r.department || '',
  first_in: (r: any) => timeKey(r.first_in_at),
  last_out: (r: any) => timeKey(r.last_out_at),
};

/** UC5 — Present in office today? Came today? When did they leave? */

/** Which slice of the board the summary tiles are scoping the table to. */
type Presence = 'all' | 'in' | 'present' | 'absent';

export interface PresenceBoardProps {
  /** Changes when the operator switches node (cloud); forces a refetch. */
  nodeKey?: string;
  /**
   * `date` is the work date the board is showing — the drawer opens on THAT day rather than on
   * today, so a row read on the 10th doesn't open a log for this morning.
   */
  onOpenEmployee: (id: string, date?: string) => void;
}

export default function PresenceBoard({ nodeKey = '', onOpenEmployee }: PresenceBoardProps) {
  const { attendance } = useApi();
  const [rows, setRows] = useState<any[]>([]);
  /**
   * TODAY's work date as the backend resolves it (its cutoff hour decides when a new one
   * starts) — the picker's ceiling and what the Today button means. Deliberately NOT the
   * work date the last response came back with: that is whichever day is being VIEWED, and
   * the two used to be one state. After selecting the 10th it held the 10th, so `max` greyed
   * out every later day, and `isToday` went true for a historical board — which restarted
   * the 20s auto-refresh on it and hid the Today button that was the only way back.
   */
  const [todayDate, setTodayDate] = useState<string>('');
  /** Same value, readable inside `fetch` without making it a dependency (it refetches). */
  const todayRef = useRef('');
  /**
   * Full/half-day thresholds behind the time-inside meter, from the same Payroll Setup the
   * day statuses are classified against. Null until the first response, and on a node whose
   * backend predates the field — <TimeInsideBar> then falls back to an 8h day.
   */
  const [dayMinutes, setDayMinutes] = useState<{ full: number; half: number } | null>(null);
  const [date, setDate] = useState<string>(''); // selected date; '' = today (resolved by backend)
  const [loading, setLoading] = useState(true);

  // Filters. `presence` is driven by the summary tiles; search + department narrow
  // independently of it.
  const [presence, setPresence] = useState<Presence>('all');
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [error, setError] = useState('');
  const filterCol = useFilterColumn('attendance-presence');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const requested = date || undefined;
      const res = await attendance.today(requested);
      const resolved = res?.work_date || '';
      setRows(res?.employees || []);
      setDayMinutes(res?.day_minutes || null);
      setError('');
      // Only a request that named NO date — or named the day we already believe is today —
      // comes back with an answer to "what is today's work date?". A historical lookup does
      // not, and taking its answer as today's is the bug this separation exists to avoid.
      // Re-reading it on the refresh loop also carries the board across the cutoff hour.
      if (!requested || requested === todayRef.current) {
        todayRef.current = resolved;
        setTodayDate(resolved);
      }
      // Seed the picker with the resolved work date on the first (today) load.
      if (!date && resolved) setDate(resolved);
    } catch (e: any) {
      console.error('presence fetch failed', e);
      setRows([]);
      // Recorded rather than swallowed: an empty board and a failed request used to look
      // identical, and "nobody is in today" is a costly thing to get wrong.
      setError(e?.message || 'Request failed');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance, date, nodeKey]);

  // Auto-refresh only when viewing today; historical dates are static.
  const isToday = !date || date === todayDate;
  useEffect(() => {
    fetch();
    if (!isToday) return;
    const t = setInterval(fetch, 20_000);
    return () => clearInterval(t);
  }, [fetch, isToday]);

  const departments = useMemo(
    () => Array.from(new Set(rows.map((r) => r.department).filter(Boolean))).sort() as string[],
    [rows],
  );

  // Everything the text + department filters allow through. The tile counts are taken
  // from THIS set (not the raw rows) so a tile's number always equals the number of rows
  // clicking it produces.
  const scoped = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (department && (r.department || '') !== department) return false;
      if (!q) return true;
      return [r.name, r.employee_code, r.designation, r.department]
        .some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [rows, search, department]);

  const inCount = scoped.filter((r) => r.is_inside).length;
  const presentCount = scoped.filter((r) => r.status !== 'absent').length;
  const absentCount = scoped.length - presentCount;

  const filtered = useMemo(() => {
    switch (presence) {
      case 'in': return scoped.filter((r) => r.is_inside);
      case 'present': return scoped.filter((r) => r.status !== 'absent');
      case 'absent': return scoped.filter((r) => r.status === 'absent');
      default: return scoped;
    }
  }, [scoped, presence]);

  const { sorted, sort, toggleSort } = useSort(filtered, PRESENCE_SORTS);

  const { pageItems, pagerProps, pageSizeProps, setPage } = usePagination(sorted, RECORD_PAGE_SIZE);

  // Any filter or reorder starts the reader back at the top of the list — after a sort,
  // "page 3" refers to entirely different people.
  useEffect(() => { setPage(1); }, [presence, search, department, date, sort, setPage]);

  // Clicking the active tile clears it, so a tile is a toggle rather than a one-way trip.
  const toggle = (v: Presence) => setPresence((cur) => (cur === v ? 'all' : v));
  const filtersActive = presence !== 'all' || !!search || !!department;
  const clearFilters = () => { setPresence('all'); setSearch(''); setDepartment(''); };

  const activeCount = [presence !== 'all', !!search, !!department].filter(Boolean).length;

  return (
    <PageWithFilters
      filters={
        <FilterColumn
          open={filterCol.open}
          onToggle={filterCol.toggle}
          label="Presence filters"
          activeCount={activeCount}
          onClearAll={clearFilters}
        >
          <FilterSection
            title="Work date"
            action={!isToday ? (
              <button onClick={() => setDate(todayDate)} className="text-[11px] font-medium text-brand hover:underline">Today</button>
            ) : null}
          >
            <input
              type="date"
              value={date}
              max={todayDate || undefined}
              onChange={(e) => setDate(e.target.value)}
              aria-label="Work date"
              className={FILTER_CONTROL}
            />
          </FilterSection>

          <FilterSection title="Search">
            <FilterSearch value={search} onChange={setSearch} placeholder="Name, code, designation…" />
          </FilterSection>

          {departments.length > 0 && (
            <FilterSection title="Department">
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                aria-label="Filter by department"
                className={FILTER_CONTROL}
              >
                <option value="">All departments</option>
                {departments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </FilterSection>
          )}
        </FilterColumn>
      }
    >
      <StatTiles>
        <StatTile
          label="Employees"
          value={scoped.length}
          tone="neutral"
          hint={scoped.length !== rows.length ? `of ${rows.length} total` : undefined}
          active={presence === 'all'}
          onClick={() => setPresence('all')}
        />
        <StatTile
          label={isToday ? 'In office now' : 'In office'}
          value={inCount}
          tone="success"
          hint={isToday ? undefined : 'at end of day'}
          active={presence === 'in'}
          onClick={() => toggle('in')}
        />
        <StatTile
          label="Present"
          value={presentCount}
          tone="info"
          hint={isToday ? 'came in today' : 'came in'}
          active={presence === 'present'}
          onClick={() => toggle('present')}
        />
        <StatTile
          label="Absent"
          value={absentCount}
          tone="muted"
          hint="no sighting"
          active={presence === 'absent'}
          onClick={() => toggle('absent')}
        />
      </StatTiles>

      <FilterBar>
        {/* Only spells out the fraction when something is actually filtered — an unfiltered
            board reading "8 of 8" is a subtraction the reader has to do to learn nothing. */}
        <FilterCount>
          {filtersActive ? `${filtered.length} of ${rows.length}` : `${rows.length}`} employees
        </FilterCount>
        {filtersActive && (
          <button onClick={clearFilters} className={FILTER_BTN}>
            <XMarkIcon className="h-3.5 w-3.5" /> Clear
          </button>
        )}

        <FilterActions>
          <PageSizeSelect {...pageSizeProps} />
          <button onClick={fetch} className={FILTER_BTN} aria-label="Refresh">
            <ArrowPathIcon className="h-3.5 w-3.5" /> Refresh
          </button>
        </FilterActions>
      </FilterBar>

      <TableCard>
        {loading ? (
          <TableSkeleton />
        ) : error ? (
          <TableError message={error} onRetry={fetch}>Couldn&apos;t load the presence board</TableError>
        ) : rows.length === 0 ? (
          <TableEmpty>No employees enrolled yet.</TableEmpty>
        ) : filtered.length === 0 ? (
          <TableEmpty>No employees match these filters.</TableEmpty>
        ) : (
          <>
            {/* `table-fixed`: the widths then come from the header rather than from whichever
                name on THIS page happens to be longest, so paging through the roster doesn't
                shuffle the columns sideways — and it's what makes the name cell truncate.

                Percentage shares summing to 100, not rem: with fixed widths every column but
                the first sits as narrow as its content, so all the slack on a wide screen
                lands in the employee column and leaves the clock times huddled on the right.

                22% for the employee, not the 30% it used to take: with the department lifted
                into its own column the cell is a name over a short "code · designation" line,
                and the width it was holding for a three-part sub-line was width the clock
                columns — the reason anyone opens this board — were not getting. */}
            <table className="w-full min-w-[58rem] table-fixed text-[13px]">
              <colgroup>
                <col className="w-[22%]" />
                <col className="w-[13%]" />
                <col className="w-[12%]" />
                <col className="w-[8%]" />
                <col className="w-[11%]" />
                <col className="w-[11%]" />
                <col className="w-[23%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <SortableTH label="Employee" columnKey="name" sort={sort} onSort={toggleSort} />
                  <SortableTH label="Department" columnKey="department" sort={sort} onSort={toggleSort} />
                  <th className={TH}>Status</th>
                  <th className={TH}>In</th>
                  <SortableTH label="First in" columnKey="first_in" sort={sort} onSort={toggleSort} />
                  <SortableTH label="Last out" columnKey="last_out" sort={sort} onSort={toggleSort} />
                  <th className={TH}>Time inside</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((r: any) => (
                  <tr key={r.id} className={`${ROW} cursor-pointer`} onClick={() => onOpenEmployee(r.id, date || todayDate || undefined)}>
                    <td className={TD}>
                      {/* Code and designation on the sub-line: on a roster of forty the code
                          disambiguates the two Sharmas, and "Line Inspector" is what an
                          operator scanning for the right one actually recognises. Department
                          is the column next door, where it can be scanned and sorted. */}
                      <PersonCell
                        name={r.name}
                        code={r.employee_code}
                        detail={r.designation}
                        avatar={<EmployeeAvatar photoId={r.primary_photo_id} name={r.name} />}
                      />
                    </td>
                    <td className={`${TD} truncate`} title={r.department || ''}>{r.department || '—'}</td>
                    {/* Present / Absent only — the full/half/partial split is a monthly
                        payroll classification, not a live daily state. */}
                    <td className={TD}><PresenceTag status={r.status} /></td>
                    <td className={TD}>{r.is_inside ? <span className="inline-flex items-center gap-1.5 font-medium text-success"><span className="h-2 w-2 rounded-full bg-success" /> In</span> : <span className="text-muted-foreground">—</span>}</td>
                    <td className={`${TD} tabular-nums`}>{fmtTime(r.first_in_at)}</td>
                    <td className={`${TD} tabular-nums`}>{fmtTime(r.last_out_at)}</td>
                    <td className={TD}>
                      <TimeInsideBar
                        minutes={r.total_inside_minutes}
                        fullDayMinutes={dayMinutes?.full}
                        halfDayMinutes={dayMinutes?.half}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager {...pagerProps} />
          </>
        )}
      </TableCard>
    </PageWithFilters>
  );
}
